// components/Modals/XModal/XModal.js
import React, { useState, useRef, useEffect } from 'react';
import CustomEmojiPicker from './CustomEmojiPicker';
import './XModal.css';
import { useConfirmModal } from '../../../contexts/ConfirmModalContext';

const XModal = ({ 
  isOpen, 
  onClose, 
  selectedAccount, 
  showMessage, 
  API_URL 
}) => {
  // Context hook
  const { showConfirm } = useConfirmModal();
  
  // Başlangıç form state'i (değişiklik kontrolü için)
  const initialFormRef = useRef(null);

  const [tweetText, setTweetText] = useState('');
  const [tweetMedia, setTweetMedia] = useState([]);
  const [mediaPreview, setMediaPreview] = useState([]);
  const [sending, setSending] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showFormatMenu, setShowFormatMenu] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  
  // Modal içinde başlayan tıklamaları takip et
  const mouseDownInsideModal = useRef(false);
  const textareaRef = useRef(null);

  // Modal açıldığında başlangıç state'ini kaydet
  useEffect(() => {
    if (isOpen && selectedAccount) {
      setTimeout(() => {
        initialFormRef.current = JSON.stringify({
          tweetText: '',
          mediaCount: 0
        });
      }, 100);
    } else {
      initialFormRef.current = null;
    }
  }, [isOpen, selectedAccount]);

  // Form değişti mi kontrol et
  const hasFormChanged = () => {
    if (!initialFormRef.current) return false;
    
    const current = JSON.stringify({
      tweetText: tweetText,
      mediaCount: tweetMedia.length
    });
    
    return current !== initialFormRef.current;
  };

  // Modal kapalıysa render etme
  if (!isOpen || !selectedAccount) return null;

  // Unicode karakter dönüşüm fonksiyonları
  const textFormatters = {
    bold: (text) => {
      const boldMap = {
        'a': '𝗮', 'b': '𝗯', 'c': '𝗰', 'd': '𝗱', 'e': '𝗲', 'f': '𝗳', 'g': '𝗴', 'h': '𝗵', 'i': '𝗶', 'j': '𝗷',
        'k': '𝗸', 'l': '𝗹', 'm': '𝗺', 'n': '𝗻', 'o': '𝗼', 'p': '𝗽', 'q': '𝗾', 'r': '𝗿', 's': '𝘀', 't': '𝘁',
        'u': '𝘂', 'v': '𝘃', 'w': '𝘄', 'x': '𝘅', 'y': '𝘆', 'z': '𝘇',
        'A': '𝗔', 'B': '𝗕', 'C': '𝗖', 'D': '𝗗', 'E': '𝗘', 'F': '𝗙', 'G': '𝗚', 'H': '𝗛', 'I': '𝗜', 'J': '𝗝',
        'K': '𝗞', 'L': '𝗟', 'M': '𝗠', 'N': '𝗡', 'O': '𝗢', 'P': '𝗣', 'Q': '𝗤', 'R': '𝗥', 'S': '𝗦', 'T': '𝗧',
        'U': '𝗨', 'V': '𝗩', 'W': '𝗪', 'X': '𝗫', 'Y': '𝗬', 'Z': '𝗭',
        '0': '𝟬', '1': '𝟭', '2': '𝟮', '3': '𝟯', '4': '𝟰', '5': '𝟱', '6': '𝟲', '7': '𝟳', '8': '𝟴', '9': '𝟵'
      };
      return text.split('').map(char => boldMap[char] || char).join('');
    },
    italic: (text) => {
      const italicMap = {
        'a': '𝘢', 'b': '𝘣', 'c': '𝘤', 'd': '𝘥', 'e': '𝘦', 'f': '𝘧', 'g': '𝘨', 'h': '𝘩', 'i': '𝘪', 'j': '𝘫',
        'k': '𝘬', 'l': '𝘭', 'm': '𝘮', 'n': '𝘯', 'o': '𝘰', 'p': '𝘱', 'q': '𝘲', 'r': '𝘳', 's': '𝘴', 't': '𝘵',
        'u': '𝘶', 'v': '𝘷', 'w': '𝘸', 'x': '𝘹', 'y': '𝘺', 'z': '𝘻',
        'A': '𝘈', 'B': '𝘉', 'C': '𝘊', 'D': '𝘋', 'E': '𝘌', 'F': '𝘍', 'G': '𝘎', 'H': '𝘏', 'I': '𝘐', 'J': '𝘑',
        'K': '𝘒', 'L': '𝘓', 'M': '𝘔', 'N': '𝘕', 'O': '𝘖', 'P': '𝘗', 'Q': '𝘘', 'R': '𝘙', 'S': '𝘚', 'T': '𝘛',
        'U': '𝘜', 'V': '𝘝', 'W': '𝘞', 'X': '𝘟', 'Y': '𝘠', 'Z': '𝘡'
      };
      return text.split('').map(char => italicMap[char] || char).join('');
    },
    boldItalic: (text) => {
      const boldItalicMap = {
        'a': '𝙖', 'b': '𝙗', 'c': '𝙘', 'd': '𝙙', 'e': '𝙚', 'f': '𝙛', 'g': '𝙜', 'h': '𝙝', 'i': '𝙞', 'j': '𝙟',
        'k': '𝙠', 'l': '𝙡', 'm': '𝙢', 'n': '𝙣', 'o': '𝙤', 'p': '𝙥', 'q': '𝙦', 'r': '𝙧', 's': '𝙨', 't': '𝙩',
        'u': '𝙪', 'v': '𝙫', 'w': '𝙬', 'x': '𝙭', 'y': '𝙮', 'z': '𝙯',
        'A': '𝘼', 'B': '𝘽', 'C': '𝘾', 'D': '𝘿', 'E': '𝙀', 'F': '𝙁', 'G': '𝙂', 'H': '𝙃', 'I': '𝙄', 'J': '𝙅',
        'K': '𝙆', 'L': '𝙇', 'M': '𝙈', 'N': '𝙉', 'O': '𝙊', 'P': '𝙋', 'Q': '𝙌', 'R': '𝙍', 'S': '𝙎', 'T': '𝙏',
        'U': '𝙐', 'V': '𝙑', 'W': '𝙒', 'X': '𝙓', 'Y': '𝙔', 'Z': '𝙕'
      };
      return text.split('').map(char => boldItalicMap[char] || char).join('');
    },
    underline: (text) => {
      // Altı çizili için combining underline karakteri kullan
      return text.split('').map(char => char + '\u0332').join('');
    },
    strikethrough: (text) => {
      // Üstü çizili için combining strikethrough karakteri kullan
      return text.split('').map(char => char + '\u0336').join('');
    },
    monospace: (text) => {
      const monospaceMap = {
        'a': '𝚊', 'b': '𝚋', 'c': '𝚌', 'd': '𝚍', 'e': '𝚎', 'f': '𝚏', 'g': '𝚐', 'h': '𝚑', 'i': '𝚒', 'j': '𝚓',
        'k': '𝚔', 'l': '𝚕', 'm': '𝚖', 'n': '𝚗', 'o': '𝚘', 'p': '𝚙', 'q': '𝚚', 'r': '𝚛', 's': '𝚜', 't': '𝚝',
        'u': '𝚞', 'v': '𝚟', 'w': '𝚠', 'x': '𝚡', 'y': '𝚢', 'z': '𝚣',
        'A': '𝙰', 'B': '𝙱', 'C': '𝙲', 'D': '𝙳', 'E': '𝙴', 'F': '𝙵', 'G': '𝙶', 'H': '𝙷', 'I': '𝙸', 'J': '𝙹',
        'K': '𝙺', 'L': '𝙻', 'M': '𝙼', 'N': '𝙽', 'O': '𝙾', 'P': '𝙿', 'Q': '𝚀', 'R': '𝚁', 'S': '𝚂', 'T': '𝚃',
        'U': '𝚄', 'V': '𝚅', 'W': '𝚆', 'X': '𝚇', 'Y': '𝚈', 'Z': '𝚉',
        '0': '𝟶', '1': '𝟷', '2': '𝟸', '3': '𝟹', '4': '𝟺', '5': '𝟻', '6': '𝟼', '7': '𝟽', '8': '𝟾', '9': '𝟿'
      };
      return text.split('').map(char => monospaceMap[char] || char).join('');
    }
  };

  // Metin formatla
  const applyFormat = (formatType) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = tweetText.substring(start, end);

    if (!selectedText) {
      showMessage('Lütfen formatlamak için bir metin seçin', 'warning');
      return;
    }

    const formattedText = textFormatters[formatType](selectedText);
    const newText = tweetText.substring(0, start) + formattedText + tweetText.substring(end);
    
    setTweetText(newText);
    setShowFormatMenu(false);

    // Seçimi koru
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start, start + formattedText.length);
    }, 0);
  };

  // Medya seç
  const handleMediaSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Maksimum 4 medya
    if (tweetMedia.length + files.length > 4) {
      showMessage('Maksimum 4 medya ekleyebilirsiniz', 'error');
      return;
    }

    // Önizleme oluştur
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setMediaPreview(prev => [...prev, {
          file,
          preview: reader.result,
          type: file.type.startsWith('image/') ? 'image' : 'video'
        }]);
      };
      reader.readAsDataURL(file);
    });

    setTweetMedia(prev => [...prev, ...files]);
  };

  // Medya sil
  const removeMedia = (index) => {
    setTweetMedia(prev => prev.filter((_, i) => i !== index));
    setMediaPreview(prev => prev.filter((_, i) => i !== index));
  };

  // Emoji ekle
  const onEmojiClick = (emojiObject) => {
    setTweetText(prev => prev + emojiObject.emoji);
  };

  // Tweet gönder
  const sendTweet = async () => {
    if (!tweetText.trim() && tweetMedia.length === 0) {
      showMessage('Lütfen bir tweet metni veya medya ekleyin', 'error');
      return;
    }

    if (tweetText.length > 280) {
      showMessage('Tweet 280 karakteri aşamaz', 'error');
      return;
    }

    try {
      setSending(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      formData.append('accountId', selectedAccount.id);
      formData.append('text', tweetText);
      
      // Medya dosyalarını ekle
      tweetMedia.forEach((file, index) => {
        formData.append('media', file);
      });

      const response = await fetch(`${API_URL}/platforms/twitter/tweet`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Tweet gönderilemedi');
      }
      
      const result = await response.json();
      showMessage('Tweet başarıyla gönderildi!', 'success');
      
      // Tweet URL'ini aç
      if (result.tweetUrl) {
        window.open(result.tweetUrl, '_blank');
      }

      // Modal'ı kapat ve state'leri temizle
      clearAndClose();
    } catch (error) {
      console.error('Tweet gönderilirken hata:', error);
      showMessage(error.message || 'Tweet gönderilemedi', 'error');
    } finally {
      setSending(false);
    }
  };

  // State'leri temizle ve kapat
  const clearAndClose = () => {
    setTweetText('');
    setTweetMedia([]);
    setMediaPreview([]);
    setShowEmojiPicker(false);
    setShowFormatMenu(false);
    setShowPreview(false);
    mouseDownInsideModal.current = false;
    onClose();
  };

  // Modal'ı kapat (değişiklik kontrolü ile)
  const handleClose = () => {
    if (hasFormChanged()) {
      showConfirm(async () => {
        clearAndClose();
      });
    } else {
      clearAndClose();
    }
  };

  // Mouse down olayını yakala (tıklama başlangıcı)
  const handleMouseDown = (e) => {
    // Emoji picker, format menu veya preview açıksa XModal'ı kapatma
    if (showEmojiPicker || showFormatMenu || showPreview) {
      mouseDownInsideModal.current = true;
      return;
    }

    // Modal içinde mi dışında mı kontrol et
    if (e.target.closest('.x-tweet-modal') || e.target.closest('.x-preview-modal')) {
      mouseDownInsideModal.current = true;
    } else {
      mouseDownInsideModal.current = false;
    }
  };

  // Mouse up olayını yakala (tıklama sonu)
  const handleMouseUp = (e) => {
    // Emoji picker, format menu veya preview açıksa XModal'ı kapatma
    if (showEmojiPicker || showFormatMenu || showPreview) {
      mouseDownInsideModal.current = false;
      return;
    }

    // Eğer tıklama modal içinde başlamadıysa ve overlay'de bitiyorsa kapat
    if (!mouseDownInsideModal.current && !e.target.closest('.x-tweet-modal') && !e.target.closest('.x-preview-modal')) {
      handleClose();
    }
    // Reset
    mouseDownInsideModal.current = false;
  };

  // Tarih formatla (Şu an)
  const getFormattedDate = () => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const day = now.getDate();
    const monthNames = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
    const month = monthNames[now.getMonth()];
    const year = now.getFullYear();
    
    return `${hours}:${minutes} · ${day} ${month} ${year}`;
  };

  // Karakter sayısını hesapla
  const remainingChars = 280 - tweetText.length;
  const charPercentage = (tweetText.length / 280) * 100;

  return (
    <>
      <div 
        className="x-tweet-modal-overlay" 
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      >
        <div className="x-tweet-modal">
          <div className="x-tweet-modal-header">
            <div className="x-tweet-modal-account">
              {selectedAccount.profile_image_url && (
                <img 
                  src={selectedAccount.profile_image_url} 
                  alt={selectedAccount.name}
                  className="x-tweet-modal-avatar"
                />
              )}
              <div>
                <h3>{selectedAccount.name}</h3>
                <span className="x-tweet-modal-username">@{selectedAccount.username}</span>
              </div>
            </div>
            <button 
              className="x-tweet-modal-close" 
              onClick={handleClose}
            >
              ✕
            </button>
          </div>

          <div className="x-tweet-modal-body">
            <textarea
              ref={textareaRef}
              className="x-tweet-textarea"
              placeholder="Ne düşünüyorsunuz?"
              value={tweetText}
              onChange={(e) => setTweetText(e.target.value)}
              maxLength={280}
              rows={6}
            />

            {/* Medya Önizleme */}
            {mediaPreview.length > 0 && (
              <div className="x-tweet-media-preview">
                {mediaPreview.map((media, index) => (
                  <div key={index} className="x-tweet-media-item">
                    {media.type === 'image' ? (
                      <img src={media.preview} alt={`Media ${index + 1}`} />
                    ) : (
                      <video src={media.preview} controls />
                    )}
                    <button 
                      className="x-tweet-media-remove"
                      onClick={() => removeMedia(index)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Karakter Sayacı */}
            <div className="x-tweet-char-counter">
              <svg width="24" height="24" viewBox="0 0 24 24">
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  fill="none"
                  stroke="#e1e8ed"
                  strokeWidth="2"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  fill="none"
                  stroke={remainingChars < 0 ? '#f4212e' : remainingChars < 20 ? '#ffad1f' : '#1d9bf0'}
                  strokeWidth="2"
                  strokeDasharray={`${charPercentage * 0.628} 62.8`}
                  strokeLinecap="round"
                  transform="rotate(-90 12 12)"
                />
              </svg>
              <span className={remainingChars < 0 ? 'x-tweet-char-negative' : ''}>
                {remainingChars < 20 ? remainingChars : ''}
              </span>
            </div>
          </div>

          <div className="x-tweet-modal-footer">
            <div className="x-tweet-actions">
              {/* Medya Ekle */}
              <label 
                className="x-tweet-action-btn" 
                title="Medya ekle"
              >
                <input
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  onChange={handleMediaSelect}
                  style={{ display: 'none' }}
                  disabled={tweetMedia.length >= 4}
                />
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M3 5.5C3 4.119 4.119 3 5.5 3h13C19.881 3 21 4.119 21 5.5v13c0 1.381-1.119 2.5-2.5 2.5h-13C4.119 21 3 19.881 3 18.5v-13zM5.5 5c-.276 0-.5.224-.5.5v9.086l3-3 3 3 5-5 3 3V5.5c0-.276-.224-.5-.5-.5h-13zM19 15.414l-3-3-5 5-3-3-3 3V18.5c0 .276.224.5.5.5h13c.276 0 .5-.224.5-.5v-3.086zM9.75 7C8.784 7 8 7.784 8 8.75s.784 1.75 1.75 1.75 1.75-.784 1.75-1.75S10.716 7 9.75 7z"/>
                </svg>
              </label>

              {/* Format Menu */}
              <div className="x-format-menu-wrapper">
                <button 
                  className="x-tweet-action-btn" 
                  onClick={() => setShowFormatMenu(!showFormatMenu)}
                  title="Metin formatla"
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M5 4h14a1 1 0 011 1v3h-2V6H6v12h5v2H5a1 1 0 01-1-1V5a1 1 0 011-1zm9.293 5.707l1.414-1.414 5 5-5 5-1.414-1.414L17.586 14H11v-2h6.586l-3.293-3.293z"/>
                  </svg>
                </button>

                {showFormatMenu && (
                  <div className="x-format-menu">
                    <button 
                      className="x-format-btn"
                      onClick={() => applyFormat('bold')}
                      title="Kalın"
                    >
                      <span style={{ fontWeight: 'bold' }}>𝗕</span>
                    </button>
                    <button 
                      className="x-format-btn"
                      onClick={() => applyFormat('italic')}
                      title="İtalik"
                    >
                      <span style={{ fontStyle: 'italic' }}>𝘐</span>
                    </button>
                    <button 
                      className="x-format-btn"
                      onClick={() => applyFormat('boldItalic')}
                      title="Kalın İtalik"
                    >
                      <span style={{ fontWeight: 'bold', fontStyle: 'italic' }}>𝘽𝙄</span>
                    </button>
                    <button 
                      className="x-format-btn"
                      onClick={() => applyFormat('underline')}
                      title="Altı Çizili"
                    >
                      <span style={{ textDecoration: 'underline' }}>U̲</span>
                    </button>
                    <button 
                      className="x-format-btn"
                      onClick={() => applyFormat('strikethrough')}
                      title="Üstü Çizili"
                    >
                      <span style={{ textDecoration: 'line-through' }}>S̶</span>
                    </button>
                    <button 
                      className="x-format-btn"
                      onClick={() => applyFormat('monospace')}
                      title="Monospace"
                    >
                      <span style={{ fontFamily: 'monospace' }}>𝙼</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Emoji Picker Button */}
              <button 
                className="x-tweet-action-btn" 
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                title="Emoji ekle"
              >
                😀
              </button>
            </div>

            <div className="x-tweet-footer-buttons">
              {/* Önizle Butonu */}
              <button 
                className="x-tweet-preview-btn"
                onClick={() => setShowPreview(true)}
                disabled={!tweetText.trim() && mediaPreview.length === 0}
                title="Tweet önizlemesi"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                </svg>
                Önizle
              </button>

              {/* Tweet Gönder Butonu */}
              <button 
                className="x-tweet-send-btn"
                onClick={sendTweet}
                disabled={sending || (!tweetText.trim() && tweetMedia.length === 0) || tweetText.length > 280}
              >
                {sending ? (
                  <>
                    <span className="spinner-small"></span>
                    Gönderiliyor...
                  </>
                ) : (
                  'Tweet Gönder'
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Custom Emoji Picker */}
        {showEmojiPicker && (
          <CustomEmojiPicker
            onEmojiClick={onEmojiClick}
            onClose={() => setShowEmojiPicker(false)}
          />
        )}
      </div>

      {/* Tweet Önizleme Modal */}
      {showPreview && (
        <div className="x-preview-overlay" onClick={() => setShowPreview(false)}>
          <div className="x-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="x-preview-header">
              <h3>Tweet Önizlemesi</h3>
              <button 
                className="x-preview-close"
                onClick={() => setShowPreview(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="x-preview-body">
              {/* X Tweet Kartı */}
              <div className="x-preview-tweet-card">
                {/* Tweet Header */}
                <div className="x-preview-tweet-header">
                  <img 
                    src={selectedAccount.profile_image_url || 'https://abs.twimg.com/sticky/default_profile_images/default_profile_400x400.png'} 
                    alt={selectedAccount.name}
                    className="x-preview-avatar"
                  />
                  <div className="x-preview-user-info">
                    <div className="x-preview-name-row">
                      <span className="x-preview-name">{selectedAccount.name}</span>
                      <svg viewBox="0 0 22 22" aria-label="Doğrulanmış hesap" className="x-preview-verified">
                        <g>
                          <path d="M20.396 11c-.018-.646-.215-1.275-.57-1.816-.354-.54-.852-.972-1.438-1.246.223-.607.27-1.264.14-1.897-.131-.634-.437-1.218-.882-1.687-.47-.445-1.053-.75-1.687-.882-.633-.13-1.29-.083-1.897.14-.273-.587-.704-1.086-1.245-1.44S11.647 1.62 11 1.604c-.646.017-1.273.213-1.813.568s-.969.854-1.24 1.44c-.608-.223-1.267-.272-1.902-.14-.635.13-1.22.436-1.69.882-.445.47-.749 1.055-.878 1.688-.13.633-.08 1.29.144 1.896-.587.274-1.087.705-1.443 1.245-.356.54-.555 1.17-.574 1.817.02.647.218 1.276.574 1.817.356.54.856.972 1.443 1.245-.224.606-.274 1.263-.144 1.896.13.634.433 1.218.877 1.688.47.443 1.054.747 1.687.878.633.132 1.29.084 1.897-.136.274.586.705 1.084 1.246 1.439.54.354 1.17.551 1.816.569.647-.016 1.276-.213 1.817-.567s.972-.854 1.245-1.44c.604.239 1.266.296 1.903.164.636-.132 1.22-.447 1.68-.907.46-.46.776-1.044.908-1.681s.075-1.299-.165-1.903c.586-.274 1.084-.705 1.439-1.246.354-.54.551-1.17.569-1.816zM9.662 14.85l-3.429-3.428 1.293-1.302 2.072 2.072 4.4-4.794 1.347 1.246z"></path>
                        </g>
                      </svg>
                    </div>
                    <span className="x-preview-username">@{selectedAccount.username}</span>
                  </div>
                  <div className="x-preview-menu-btn">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                      <path d="M3 12c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm9 2c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm7 0c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"></path>
                    </svg>
                  </div>
                </div>

                {/* Tweet Content */}
                <div className="x-preview-tweet-content">
                  <p className="x-preview-text">{tweetText}</p>
                  
                  {/* Medya Önizleme */}
                  {mediaPreview.length > 0 && (
                    <div className={`x-preview-media-grid x-preview-media-count-${mediaPreview.length}`}>
                      {mediaPreview.map((media, index) => (
                        <div key={index} className="x-preview-media-item">
                          {media.type === 'image' ? (
                            <img src={media.preview} alt={`Media ${index + 1}`} />
                          ) : (
                            <video src={media.preview} controls />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Tweet Footer */}
                <div className="x-preview-tweet-footer">
                  <span className="x-preview-date">{getFormattedDate()}</span>
                  <span className="x-preview-divider">·</span>
                  <span className="x-preview-views">
                    <strong>0</strong> Görüntülenme
                  </span>
                </div>

                {/* Tweet Actions */}
                <div className="x-preview-tweet-actions">
                  <button className="x-preview-action-btn">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                      <path d="M1.751 10c0-4.42 3.584-8 8.005-8h4.366c4.49 0 8.129 3.64 8.129 8.13 0 2.96-1.607 5.68-4.196 7.11l-8.054 4.46v-3.69h-.067c-4.49.1-8.183-3.51-8.183-8.01zm8.005-6c-3.317 0-6.005 2.69-6.005 6 0 3.37 2.77 6.08 6.138 6.01l.351-.01h1.761v2.3l5.087-2.81c1.951-1.08 3.163-3.13 3.163-5.36 0-3.39-2.744-6.13-6.129-6.13H9.756z"></path>
                    </svg>
                    <span>0</span>
                  </button>
                  <button className="x-preview-action-btn">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                      <path d="M4.5 3.88l4.432 4.14-1.364 1.46L5.5 7.55V16c0 1.1.896 2 2 2H13v2H7.5c-2.209 0-4-1.79-4-4V7.55L1.432 9.48.068 8.02 4.5 3.88zM16.5 6H11V4h5.5c2.209 0 4 1.79 4 4v8.45l2.068-1.93 1.364 1.46-4.432 4.14-4.432-4.14 1.364-1.46 2.068 1.93V8c0-1.1-.896-2-2-2z"></path>
                    </svg>
                    <span>0</span>
                  </button>
                  <button className="x-preview-action-btn">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                      <path d="M16.697 5.5c-1.222-.06-2.679.51-3.89 2.16l-.805 1.09-.806-1.09C9.984 6.01 8.526 5.44 7.304 5.5c-1.243.07-2.349.78-2.91 1.91-.552 1.12-.633 2.78.479 4.82 1.074 1.97 3.257 4.27 7.129 6.61 3.87-2.34 6.052-4.64 7.126-6.61 1.111-2.04 1.03-3.7.477-4.82-.561-1.13-1.666-1.84-2.908-1.91zm4.187 7.69c-1.351 2.48-4.001 5.12-8.379 7.67l-.503.3-.504-.3c-4.379-2.55-7.029-5.19-8.382-7.67-1.36-2.5-1.41-4.86-.514-6.67.887-1.79 2.647-2.91 4.601-3.01 1.651-.09 3.368.56 4.798 2.01 1.429-1.45 3.146-2.1 4.796-2.01 1.954.1 3.714 1.22 4.601 3.01.896 1.81.846 4.17-.514 6.67z"></path>
                    </svg>
                    <span>0</span>
                  </button>
                  <button className="x-preview-action-btn">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                      <path d="M8.75 21V3h2v18h-2zM18 21V8.5h2V21h-2zM4 21l.004-10h2L6 21H4zm9.248 0v-7h2v7h-2z"></path>
                    </svg>
                    <span>0</span>
                  </button>
                  <button className="x-preview-action-btn">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                      <path d="M12 2.59l5.7 5.7-1.41 1.42L13 6.41V16h-2V6.41l-3.3 3.3-1.41-1.42L12 2.59zM21 15l-.02 3.51c0 1.38-1.12 2.49-2.5 2.49H5.5C4.11 21 3 19.88 3 18.5V15h2v3.5c0 .28.22.5.5.5h12.98c.28 0 .5-.22.5-.5L19 15h2z"></path>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default XModal;